/**
 * InventoryWalkPanel — Inventur-Rundgang (Jun-2026, User-Wunsch).
 *
 * Schneller Voll-Rundgang per Scanner. Zwei Modi:
 *   mode="full"   → je Fahrzeug Fahrzeug-QR (Vorne/Hinten) UND Schlüssel-QR
 *                   scannen. Vollständig = beide gescannt. (Scan-Seite)
 *   mode="papers" → je Fahrzeug den Papiercode scannen (Papiere liegen woanders,
 *                   daher separater Rundgang in der Disposition).
 *
 * Am Ende: Auswertung mit „fehlenden Positionen". Macht eine Vollinventur in
 * Minuten statt Stunden.
 */
import { useState, useRef, useEffect, useMemo } from "react";
import { ClipboardCheck, ScanLine, Check, Loader2, Play, Flag, Car, Key, FileText, AlertTriangle, RotateCcw, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { previewPdfResponse } from "../../lib/pdfPreview";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export default function InventoryWalkPanel({ mode = "full", onClose }) {
    const isPapers = mode === "papers";
    const [started, setStarted] = useState(false);
    const [finished, setFinished] = useState(false);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [scans, setScans] = useState({});
    const [scanVal, setScanVal] = useState("");
    const [lastScan, setLastScan] = useState(null);
    const [startedAt, setStartedAt] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => { if (started && !finished && inputRef.current) inputRef.current.focus(); }, [started, finished, lastScan]);

    const start = async () => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/inventory-walk/vehicles");
            let items = r.data.items || [];
            if (isPapers) items = items.filter((v) => (v.papers_inventory_code || "").trim());
            setVehicles(items);
            setScans({});
            setLastScan(null);
            setStartedAt(r.data.started_at || new Date().toISOString());
            setFinished(false);
            setStarted(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rundgang konnte nicht gestartet werden");
        } finally { setLoading(false); }
    };

    const isComplete = (id) => {
        const s = scans[id] || {};
        return isPapers ? !!s.papers : (!!s.vehicle && !!s.key);
    };

    const completeCount = useMemo(() => vehicles.filter((v) => isComplete(v.id)).length, [vehicles, scans]);  // eslint-disable-line react-hooks/exhaustive-deps
    const total = vehicles.length;
    const pct = total ? Math.round((completeCount / total) * 100) : 0;

    const handleScan = async () => {
        const code = scanVal.trim();
        if (!code) return;
        setBusy(true);
        try {
            const r = await api.post("/dealer/inventory-walk/scan", { code });
            const { listing_id, title, kind } = r.data;
            if (isPapers && kind !== "papers") {
                setLastScan({ ok: false, title, msg: "Kein Papiercode — bitte den Code der Fahrzeugpapiere scannen." });
            } else if (!isPapers && kind === "papers") {
                setLastScan({ ok: false, title, msg: "Papiercode erkannt — gehört zum Papier-Rundgang in der Disposition." });
            } else {
                setScans((prev) => ({ ...prev, [listing_id]: { ...(prev[listing_id] || {}), [kind]: true } }));
                const label = kind === "vehicle" ? "Fahrzeug" : kind === "key" ? "Schlüssel" : "Papiere";
                setLastScan({ ok: true, title, kind, msg: `${label} ✓` });
            }
            setScanVal("");
        } catch (e) {
            setLastScan({ ok: false, title: code, msg: e.response?.data?.detail || "Kein Treffer" });
            setScanVal("");
        } finally { setBusy(false); }
    };

    const missing = useMemo(() => vehicles.filter((v) => !isComplete(v.id)).map((v) => {
        const s = scans[v.id] || {};
        const lacks = isPapers ? ["Papiere"] : [!s.vehicle && "Fahrzeug", !s.key && "Schlüssel"].filter(Boolean);
        return { ...v, lacks };
    }), [vehicles, scans]);  // eslint-disable-line react-hooks/exhaustive-deps

    const sortedVehicles = useMemo(() => [...vehicles].sort((a, b) => Number(isComplete(a.id)) - Number(isComplete(b.id))), [vehicles, scans]);  // eslint-disable-line react-hooks/exhaustive-deps

    const [pdfBusy, setPdfBusy] = useState(false);
    const downloadProtocol = async () => {
        setPdfBusy(true);
        try {
            const r = await api.post("/dealer/inventory-walk/protocol.pdf", {
                mode,
                total,
                complete: completeCount,
                started_at: startedAt,
                missing: missing.map((m) => ({ title: m.title, lacks: m.lacks })),
            }, { responseType: "blob" });
            previewPdfResponse(r, `Inventur-Protokoll_${mode}.pdf`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Protokoll konnte nicht erstellt werden");
        } finally { setPdfBusy(false); }
    };

    const title = isPapers ? "Papier-Rundgang" : "Inventur-Rundgang (Fahrzeug + Schlüssel)";
    const Icon = isPapers ? FileText : ClipboardCheck;

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="inventory-walk-panel">
            <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="font-bold flex items-center gap-2"><Icon className="w-5 h-5 text-[#FFCC00]" /> {title}</h3>
                {onClose && <button onClick={onClose} className="text-gray-400 hover:text-black text-sm" data-testid="inventory-walk-close">✕</button>}
            </div>

            {!started && (
                <div data-testid="inventory-walk-intro">
                    <p className="text-sm text-gray-600 mb-3">
                        {isPapers
                            ? "Scannen Sie nacheinander die Papiercodes aller Fahrzeuge am Papier-Lagerort. Am Ende sehen Sie, welche Papiere fehlen."
                            : "Gehen Sie durch den Bestand und scannen Sie je Fahrzeug den Fahrzeug-QR (Vorne/Hinten) UND den Schlüssel-QR. Am Ende sehen Sie fehlende Fahrzeuge & Schlüssel."}
                    </p>
                    <Button onClick={start} disabled={loading} className="rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="inventory-walk-start-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1.5" /> Rundgang starten</>}
                    </Button>
                </div>
            )}

            {started && !finished && (
                <div>
                    {/* Fortschritt */}
                    <div className="mb-3" data-testid="inventory-walk-progress">
                        <div className="flex justify-between text-xs font-bold text-gray-600 mb-1">
                            <span>{completeCount} / {total} vollständig</span>
                            <span>{pct}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#FFCC00] transition-all" style={{ width: `${pct}%` }} />
                        </div>
                    </div>

                    {/* Scan */}
                    <div className="flex gap-2 mb-2">
                        <Input
                            ref={inputRef}
                            value={scanVal}
                            onChange={(e) => setScanVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleScan(); } }}
                            placeholder={isPapers ? "Papiercode scannen…" : "Fahrzeug- oder Schlüssel-QR scannen…"}
                            className="h-10 rounded-sm font-mono-num"
                            data-testid="inventory-walk-scan-input"
                            autoFocus
                        />
                        <Button onClick={handleScan} disabled={busy} className="h-10 rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="inventory-walk-scan-btn">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
                        </Button>
                    </div>

                    {/* Letzter Scan */}
                    {lastScan && (
                        <div className={`text-sm mb-3 px-3 py-2 rounded-sm ${lastScan.ok ? "bg-green-50 text-green-800" : "bg-rose-50 text-rose-700"}`} data-testid="inventory-walk-last-scan">
                            {lastScan.ok ? <Check className="w-4 h-4 inline mr-1" /> : <AlertTriangle className="w-4 h-4 inline mr-1" />}
                            <strong>{lastScan.title}</strong> — {lastScan.msg}
                        </div>
                    )}

                    {/* Checkliste */}
                    <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-sm divide-y" data-testid="inventory-walk-checklist">
                        {sortedVehicles.map((v) => {
                            const s = scans[v.id] || {};
                            const done = isComplete(v.id);
                            return (
                                <div key={v.id} className={`flex items-center justify-between gap-2 px-3 py-1.5 text-[12px] ${done ? "bg-green-50/50" : ""}`} data-testid={`inventory-walk-row-${v.id}`}>
                                    <span className="truncate font-medium text-gray-800">{v.title}</span>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        {isPapers ? (
                                            <Badge on={!!s.papers} icon={FileText} label="Papiere" />
                                        ) : (
                                            <>
                                                <Badge on={!!s.vehicle} icon={Car} label="Fzg" />
                                                <Badge on={!!s.key} icon={Key} label="Schl." />
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-3 flex justify-end">
                        <Button onClick={() => setFinished(true)} variant="outline" className="rounded-sm" data-testid="inventory-walk-finish-btn">
                            <Flag className="w-4 h-4 mr-1.5" /> Beenden & Auswertung
                        </Button>
                    </div>
                </div>
            )}

            {finished && (
                <div data-testid="inventory-walk-summary">
                    <div className={`rounded-sm p-3 mb-3 ${missing.length === 0 ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"}`}>
                        {missing.length === 0
                            ? <><Check className="w-5 h-5 inline mr-1" /> <strong>Vollständig!</strong> Alle {total} Positionen erfasst.</>
                            : <><AlertTriangle className="w-5 h-5 inline mr-1" /> <strong>{completeCount} / {total} vollständig</strong> — {missing.length} fehlen:</>}
                    </div>
                    {missing.length > 0 && (
                        <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-sm divide-y mb-3">
                            {missing.map((v) => (
                                <div key={v.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-[12px]" data-testid={`inventory-walk-missing-${v.id}`}>
                                    <span className="truncate font-medium text-gray-800">{v.title}</span>
                                    <span className="text-rose-600 font-bold shrink-0">fehlt: {v.lacks.join(" + ")}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex justify-end gap-2">
                        <Button onClick={downloadProtocol} disabled={pdfBusy} className="rounded-sm bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-[#111827] font-bold" data-testid="inventory-walk-protocol-btn">
                            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1.5" /> Protokoll als PDF</>}
                        </Button>
                        <Button onClick={start} variant="outline" className="rounded-sm" data-testid="inventory-walk-reset-btn">
                            <RotateCcw className="w-4 h-4 mr-1.5" /> Neu starten
                        </Button>
                        {onClose && <Button onClick={onClose} className="rounded-sm bg-[#111827] text-white">Schließen</Button>}
                    </div>
                </div>
            )}
        </div>
    );
}

const Badge = ({ on, icon: I, label }) => (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${on ? "bg-green-600 text-white" : "bg-gray-100 text-gray-400"}`}>
        <I className="w-3 h-3" /> {label} {on ? "✓" : "—"}
    </span>
);
